import { useTranslation } from 'react-i18next';

import { useGetAvailableCompaniesQuery } from '@crm/entities/company';
import UpDownIcon from '@crm/shared/assets/icons/updown.svg?react';
import { cn } from '@crm/shared/lib';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
    Skeleton,
    SvgIcon,
    Tooltip,
    TooltipContent,
    TooltipTrigger,
} from '@crm/shared/ui';
import { Spinner } from '@crm/shared/ui/Spinner';

import { CompanyItem } from './CompanyItem';
import { useCompanySwitch } from '../model/useCompanySwitch';

export const CompanySwitcher = () => {
    const { t } = useTranslation();
    const {
        data: companies = [],
        isError,
        isFetching,
        isPending,
        refetch,
    } = useGetAvailableCompaniesQuery();
    const { isPending: isSettingActiveCompany, switchCompany } = useCompanySwitch();
    const activeCompany = companies.find((company) => company.isActive);
    const renderContent = () => {
        if (isPending) {
            return (
                <li role="none" className="flex justify-center px-4 py-2">
                    <Spinner />
                </li>
            );
        }

        if (isError && companies.length === 0) {
            return (
                <DropdownMenuItem
                    render={<li />}
                    closeOnClick={false}
                    disabled={isFetching}
                    className="justify-center px-4 py-2"
                    onClick={() => refetch()}
                >
                    {isFetching ? t('loading.text') : t('button.retry')}
                </DropdownMenuItem>
            );
        }

        return companies.map((company) => (
            <DropdownMenuItem
                key={company.id}
                render={<li />}
                disabled={isSettingActiveCompany}
                className={cn('p-0', company.isOwner && 'rounded-b-none border-b border-zinc-300')}
                onClick={() => switchCompany(company)}
            >
                <CompanyItem {...company} />
            </DropdownMenuItem>
        ));
    };

    return (
        <DropdownMenu>
            <Tooltip>
                <TooltipTrigger render={<div className="flex self-stretch" />}>
                    <DropdownMenuTrigger
                        aria-label={t('crm.companySwitcher.triggerAriaLabel')}
                        className="group flex cursor-pointer items-center justify-center gap-0.5 rounded-xs py-0 pr-0 pl-2.25 hover:bg-smtr-main-gray-100"
                    >
                        {isPending || isSettingActiveCompany ? (
                            <Skeleton width={80} height={28} />
                        ) : (
                            <div className="flex translate-y-0.5 flex-col items-start">
                                <span className="text-xs leading-[15px] font-medium text-smtr-main-black-100">
                                    {activeCompany?.title}
                                </span>
                                <span className="text-[10px] leading-4 text-smtr-main-tertiary-100/40">
                                    {activeCompany?.ownerEmail}
                                </span>
                            </div>
                        )}
                        {!isPending && (
                            <SvgIcon
                                icon={UpDownIcon}
                                className="size-4 text-[#B8B8BA] group-hover:text-smtr-main-black-100"
                            />
                        )}
                    </DropdownMenuTrigger>
                </TooltipTrigger>
                <TooltipContent side="right">{t('crm.companySwitcher.tooltip')}</TooltipContent>
            </Tooltip>
            <DropdownMenuContent
                render={<ul />}
                className="m-0 flex w-max max-w-75 min-w-56.25 list-none flex-col rounded-md py-1 pr-0 pl-0"
            >
                {renderContent()}
            </DropdownMenuContent>
        </DropdownMenu>
    );
};
